"use client"

import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { Link } from "react-router-dom"
import { Save as SaveIcon, Visibility as VisibilityIcon } from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import axios from "axios"
import { API_URL } from "../constants"
import ReactMarkdown from "react-markdown"

const AdminTermsPage = () => {
  const navigate = useNavigate()
  const { userInfo } = useSelector((state) => state.auth)
  const [activeTab, setActiveTab] = useState(0)
  const [content, setContent] = useState("")
  const [preview, setPreview] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  useEffect(() => {
    // Check if user is admin
    if (userInfo?.user?.role !== "admin") {
      navigate("/terms")
      return
    }

    const fetchTerms = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/terms`)

        // If content is empty or just the default message, set our default content
        if (!data.content || data.content === "Terms and conditions have not been set up yet.") {
          const defaultContent = `# WhatsApp Campaign Terms & Conditions

## WhatsApp Campaign Limitations:

1. All WhatsApp Campaigns sent may not be visible on Web WhatsApp.
2. WhatsApp Campaign may not reach iPhones all the time, due to iPhone security issues.
3. Every number sent from our Panel will show as SENT only always in reports.
4. The delivery success ratio will be approximately 60% to 80%, because 20%-40% may not get delivered or may show waiting for message, due to channel (WhatsApp number) Ban issues in WhatsApp.
5. Buttons Campaign will be allowed with 01 Media Only (01 image or 01 video or 01 PDF).
6. Buttons Campaigns are available for INDIA only.
7. Button Campaign may take a minimum of 30-240 minutes to deliver.
8. No Elections Campaigns allowed for Button Campaign.
9. Max Allowed size for image/video/pdf is 5MB each only.
10. Max up to 02 Lacs numbers can be sent in a single campaign.
11. Delivery time 02-04 Hrs.
12. Can be sent Monday to Saturday between 10 am to 06 pm IST only.
13. Max 03 Demo campaigns (less than 10 numbers) allowed in a day.
14. Max 10 campaigns allowed in a day.
15. All non-WhatsApp number credits would be refunded after 48 Hrs.

Note: Before pushing anything in bulk, please test by sending to your own 5-10 numbers to ensure everything is fine because once sent cannot be undone.`

          setContent(defaultContent)
          setPreview(defaultContent)
        } else {
          setContent(data.content)
          setPreview(data.content)
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load terms and conditions")
      } finally {
        setLoading(false)
      }
    }

    fetchTerms()
  }, [userInfo, navigate])

  const fetchHistory = async () => {
    if (history.length > 0) return // Only fetch once

    setLoadingHistory(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }
      const { data } = await axios.get(`${API_URL}/api/terms/history`, config)
      setHistory(data)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load terms history")
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    if (newValue === 1) {
      // Preview tab
      setPreview(content)
    } else if (newValue === 2) {
      // History tab
      fetchHistory()
    }
    setActiveTab(newValue)
  }

  const handleContentChange = (e) => {
    setContent(e.target.value)
  }

  const handleSave = async () => {
    if (!content.trim()) {
      setError("Content cannot be empty")
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      await axios.put(`${API_URL}/api/terms`, { content }, config)
      setSuccess("Terms and conditions updated successfully")

      // Refresh history if on history tab
      if (activeTab === 2) {
        fetchHistory()
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update terms and conditions")
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = () => {
    setPreview(content)
    setActiveTab(1)
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </MuiLink>
          <MuiLink component={Link} to="/terms" color="inherit" underline="hover">
            Terms & Conditions
          </MuiLink>
          <Typography color="text.primary">Manage</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Terms & Conditions
          </Typography>
          <Button component={Link} to="/terms" variant="outlined" startIcon={<VisibilityIcon />}>
            View Public Page
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ mb: 4 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tab label="Edit" />
            <Tab label="Preview" />
            <Tab label="History" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You can use Markdown formatting for rich text. Changes will be saved as a new version.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const defaultContent = `# WhatsApp Campaign Terms & Conditions

## WhatsApp Campaign Limitations:

1. All WhatsApp Campaigns sent may not be visible on Web WhatsApp.
2. WhatsApp Campaign may not reach iPhones all the time, due to iPhone security issues.
3. Every number sent from our Panel will show as SENT only always in reports.
4. The delivery success ratio will be approximately 60% to 80%, because 20%-40% may not get delivered or may show waiting for message, due to channel (WhatsApp number) Ban issues in WhatsApp.
5. Buttons Campaign will be allowed with 01 Media Only (01 image or 01 video or 01 PDF).
6. Buttons Campaigns are available for INDIA only.
7. Button Campaign may take a minimum of 30-240 minutes to deliver.
8. No Elections Campaigns allowed for Button Campaign.
9. Max Allowed size for image/video/pdf is 5MB each only.
10. Max up to 02 Lacs numbers can be sent in a single campaign.
11. Delivery time 02-04 Hrs.
12. Can be sent Monday to Saturday between 10 am to 06 pm IST only.
13. Max 03 Demo campaigns (less than 10 numbers) allowed in a day.
14. Max 10 campaigns allowed in a day.
15. All non-WhatsApp number credits would be refunded after 48 Hrs.

Note: Before pushing anything in bulk, please test by sending to your own 5-10 numbers to ensure everything is fine because once sent cannot be undone.`
                          setContent(defaultContent)
                        }}
                      >
                        Insert Default Content
                      </Button>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={20}
                      value={content}
                      onChange={handleContentChange}
                      placeholder="Enter terms and conditions content here..."
                      variant="outlined"
                      sx={{ mb: 3, fontFamily: "monospace" }}
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button variant="outlined" onClick={handlePreview}>
                        Preview
                      </Button>
                    </Box>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Preview
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Box
                      sx={{
                        "& h1": { fontSize: "2rem", mb: 3, mt: 2, color: "#2e3b55", fontWeight: "bold" },
                        "& h2": { fontSize: "1.5rem", mb: 2, mt: 3, color: "#2e3b55", fontWeight: "bold" },
                        "& h3": { fontSize: "1.3rem", mb: 1.5, mt: 2.5 },
                        "& p": { mb: 2 },
                        "& ul, & ol": { mb: 2, pl: 4 },
                        "& li": { mb: 1, lineHeight: 1.6 },
                        "& ol": { counterReset: "item" },
                        "& ol > li": {
                          display: "block",
                          position: "relative",
                          paddingLeft: "1.5em",
                          marginBottom: "0.5em",
                        },
                        "& ol > li::before": {
                          content: "counter(item) '.'",
                          counterIncrement: "item",
                          position: "absolute",
                          left: 0,
                          fontWeight: "bold",
                        },
                        "& blockquote": {
                          borderLeft: "4px solid #ccc",
                          paddingLeft: "1em",
                          fontStyle: "italic",
                          color: "#555",
                        },
                      }}
                    >
                      <ReactMarkdown>{preview}</ReactMarkdown>
                    </Box>
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Version History
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {loadingHistory ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : history.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Version</TableCell>
                              <TableCell>Last Updated</TableCell>
                              <TableCell>Updated By</TableCell>
                              <TableCell>Content Preview</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history.map((version, index) => (
                              <TableRow key={version._id}>
                                <TableCell>{history.length - index}</TableCell>
                                <TableCell>
                                  {new Date(version.lastUpdated).toLocaleDateString()}{" "}
                                  {new Date(version.lastUpdated).toLocaleTimeString()}
                                </TableCell>
                                <TableCell>{version.updatedBy ? version.updatedBy.name : "System"}</TableCell>
                                <TableCell>
                                  {version.content.substring(0, 100)}
                                  {version.content.length > 100 ? "..." : ""}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography>No history available.</Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  )
}

export default AdminTermsPage

